import { locationData } from "./locationData";
import passwordValidator from 'password-validator';
import { buildURI } from "./csv-generator";
import { ShowToast } from "./toast";
import moment from "moment";

// Create a password schema
var passwordSchema = (new passwordValidator())
  .is().min(8)                                    // Minimum length 8
  .is().max(100)                                  // Maximum length 100
  // .has().uppercase(1)                              // Must have uppercase letters
  .has().lowercase(1)                              // Must have lowercase letters
  .has().digits(1)                                // Must have at least 1 digit
  .has().symbols(1)                               // Must have at least 1 symbol

export const setToken = (token: string) => {
  // set to local storage
  localStorage.setItem("token", token);
};

export const setUser = (user: any) => {
  // set to local storage
  localStorage.setItem("user", JSON.stringify(user));
};

export const getUser = () => {
  // get from local storage
  const user = localStorage.getItem("user");
  return user ? JSON.parse(user) : null;
};

export const getConsent = () => {
  // get from local storage
  const consent = localStorage.getItem("consent");
  return consent ? JSON.parse(consent) : null;
};

export const setConsent = (consent: any) => {
  // set to local storage
  localStorage.setItem("consent", JSON.stringify(consent));
};

export function checkPasswordStrength(password) {
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  const requirements = [
    { regex: /[a-z]/, message: "lowercase letter" },
    { regex: /[A-Z]/, message: "uppercase letter" },
    { regex: /\d/, message: "digit" },
    { regex: /[@$!%*?&]/, message: "special character" },
    { regex: /.{8,}/, message: "at least 8 characters" },
  ];
  let score = 0;
  let message = "";
  requirements.forEach((requirement) => {
    if (requirement.regex.test(password)) {
      score++;
    } else {
      message += ` ${requirement.message},`;
    }
  });
  if (score === requirements.length) {
    message = "Your password is great. Nice work!";
  } else {
    message = `Your password needs ${message.slice(0, -1)}`;
  }
  return { score: (score / requirements.length) * 100, message };
}

export const validateEmail = (email: string): boolean => {
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email);
};

export const clearTokenAndUser = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
};

export const validatePhone = (phone: string): boolean => {
  return !!phone.match(/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/);
};

export default function passwordValidation(password: string) {
  return passwordSchema.validate(password);
}

export function formatNumber(num: number) {
  const absNum = Math.abs(num);

  if (absNum >= 1e9) {
    return (num / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
  } else if (absNum >= 1e6) {
    return (num / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (absNum >= 1e3) {
    return (num / 1e3).toFixed(1).replace(/\.0$/, "") + "k";
  } else {
    return num.toString();
  }
}

export function isMe(type: string, isPatientSender: boolean) {
  // type = "P - Provider" or "PF - Patient"
  // isPatientSender = true & type = "PF" => true
  // isPatientSender = false & type = "P" => true
  return (!isPatientSender && type === "PF") || (isPatientSender && type === "P");
}

export const getLocationListForSelect = () => {
  const locationList: { label: string; value: string }[] = locationData.map((location) => ({
    label: location,
    value: location,
  }));
  return locationList;
};

export function getInitial(firstName: string, lastName: string) {
  // Akshay Kumar => AK
  // Akshay => A
  // Kumar => K
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`;
  } else if (firstName) {
    return firstName[0];
  } else if (lastName) {
    return lastName[0];
  } else {
    return "";
  }
}

export const youtubeParser = (url) => {
  var regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  var match = url.match(regExp);
  return match && match[7].length == 11 ? match[7] : false;
};

// Combined address helper for Admin tables/exports
export type ClientAddressParts = {
  clientAddress1?: string | null;
  clientAddress2?: string | null;
  clientCity?: string | null;
  clientState?: string | null;
  clientZipCode?: string | null;
};

/**
 * Formats a single-line postal address.
 * Example: "123 Main St Apt 5B, San Francisco, CA 94107"
 */
export function formatClientAddress(parts?: ClientAddressParts, fallback: string = ""): string {
  if (!parts) return fallback;

  const a1 = (parts.clientAddress1 || "").trim();
  const a2 = (parts.clientAddress2 || "").trim();
  const city = (parts.clientCity || "").trim();
  const state = (parts.clientState || "").trim();
  const zip = (parts.clientZipCode || "").trim();

  const primary = [a1, a2].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  const cityState = [city, state].filter(Boolean).join(", ");
  const cityStateZip = cityState ? `${cityState}${zip ? ` ${zip}` : ""}` : zip;

  const result = [primary, cityStateZip].filter(Boolean).join(", ");
  return result || fallback;
}

// User address helper for Manage Clients table with backward compatibility
export type UserAddressParts = {
  addressLine1?: string | null;
  addressLine2?: string | null; 
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
};

/**
 * Formats a user's complete address with backward compatibility
 * Handles new address fields + fallback to zipCode lookup
 * 
 * @param user User object with address fields
 * @param zipCodes Lookup object for zipCode to city/state mapping
 * @param fallback Default value if no address data
 * @returns Formatted address string
 */
export function formatUserAddress(user?: UserAddressParts, zipCodes?: any, fallback: string = "-"): string {
  if (!user) return fallback;

  const a1 = (user.addressLine1 || "").trim();
  const a2 = (user.addressLine2 || "").trim();

  // City: Prefer new field, fallback to zipCode lookup
  const city = (user.city || "").trim() || (zipCodes && user.zipCode ? zipCodes[user.zipCode]?.city : "") || "";

  // State: Prefer new field, fallback to zipCode lookup
  // Note: New field uses full names (e.g., "California"), zipCode lookup uses abbreviations (e.g., "CA")
  const state = (user.state || "").trim() || (zipCodes && user.zipCode ? zipCodes[user.zipCode]?.state_short : "") || "";

  const zip = (user.zipCode || "").trim();

  const primary = [a1, a2].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  const cityState = [city, state].filter(Boolean).join(", ");
  const cityStateZip = cityState ? `${cityState}${zip ? ` ${zip}` : ""}` : zip;

  const result = [primary, cityStateZip].filter(Boolean).join(", ");
  return result || fallback;
}


/**
 * Generic CSV download configuration
 */
export interface CSVDownloadConfig<T> {
  data: T[];
  headers: string[];
  rowMapper: (item: T) => (string | number | null | undefined)[];
  filename?: string;
  dateFormat?: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
  noDataMessage?: string;
}

/**
 * Downloads data as a CSV file with configurable options
 * @param config Configuration object for CSV download
 * @returns Promise that resolves when download is complete
 */
export async function downloadCSV<T>(config: CSVDownloadConfig<T>): Promise<void> {
  const {
    data,
    headers,
    rowMapper,
    filename = `export-${moment().format('YYYY-MM-DD')}.csv`,
    onSuccess,
    onError,
    noDataMessage = 'No data available to download'
  } = config;

  try {
    if (!data || data.length === 0) {
      // Don't show toast for empty data - it's not an error
      console.log(noDataMessage);
      return;
    }

    // Map data rows using the provided mapper function
    const mappedData = data.map(rowMapper);

    // Add headers as first row
    const csvData = [headers, ...mappedData];

    // Generate CSV and trigger download
    const csvUri = buildURI(csvData, true, undefined, undefined, undefined);
    const link = document.createElement('a');
    link.href = csvUri;
    link.download = filename;
    link.click();
    link.remove();

    ShowToast({
      type: 'success',
      message: 'CSV downloaded successfully'
    });

    onSuccess?.();
  } catch (error) {
    console.error('Failed to download CSV:', error);
    ShowToast({
      type: 'error',
      message: error?.response?.data?.message || 'Failed to download CSV'
    });
    onError?.(error);
  }
}

/**
 * Helper function to format dates consistently for CSVs
 */
export function formatDateForCSV(dateString: string | Date | null | undefined, format = 'YYYY-MM-DD HH:mm:ss'): string {
  if (!dateString) return '-';
  return moment(dateString).format(format);
}

/**
 * Helper function to safely convert values to CSV-friendly strings
 */
export function toCSVValue(value: any): string {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toString();
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

interface PaginatedRequest {
  page?: number;
  limit?: number;
  [key: string]: unknown;
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
}

/**
 * Fetch all paginated data from an API endpoint
 * @param fetchFunction - The API function to call for each page
 * @param queryParams - Base query parameters (without page/limit)
 * @param batchSize - Number of items to fetch per request
 * @returns Array of all fetched data items
 */
export async function fetchAllPaginatedData<T>(
  fetchFunction: (params: PaginatedRequest) => Promise<PaginatedResponse<T>>,
  queryParams: Omit<PaginatedRequest, 'page' | 'limit'> = {},
  batchSize = 10000
): Promise<T[]> {
  let allData: T[] = [];
  let page = 1;
  let totalFetched = 0;

  while (true) {
    const response = await fetchFunction({
      ...queryParams,
      page,
      limit: batchSize
    });

    if (response?.data && response.data.length > 0) {
      allData = [...allData, ...response.data];
      totalFetched += response.data.length;

      // Check if we've fetched all available data
      if (totalFetched >= response.total || response.data.length < batchSize) {
        break;
      }
      page++;
    } else {
      break;
    }
  }

  return allData;
}
